import { Button, Notice, TextField } from "@sk/ui";
import { ArrowLeft, Mail, MailCheck, RotateCw, WifiOff } from "lucide-react";
import { lazy, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { allTexts } from "../i18n/index.ts";
import { type AuthErrorKey, type AuthErrorLike, authErrorKey, normalizeCode } from "../lib/authErrors.ts";
import { useInstallCard } from "../lib/install.ts";
import { isGoogleEnabled, sendEmailCode, signInWithGoogle, verifyEmailCode } from "../lib/supabase.ts";

// Carte d'installation, chargée seulement si l'on arrive de la page d'accueil (?installer=1) : ses textes sont dans fr.json.
const InstallCard = lazy(() =>
  allTexts.then(() => import("../components/InstallCard.tsx")).then((m) => ({ default: m.InstallCard })),
);

/** Délai avant de pouvoir redemander un code (limite d'envoi de Supabase Auth). */
const RESEND_DELAY_S = 60;

function useOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

/** M1 — connexion ou inscription par code reçu par e-mail (décision D8), ou par Google. */
export function Login() {
  const { t } = useTranslation();
  const online = useOnline();
  const install = useInstallCard();
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState<"send" | "verify" | "google" | null>(null);
  const [error, setError] = useState<AuthErrorKey | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  // Google reste « bientôt disponible » tant que Supabase Auth ne l'annonce pas activé.
  const [googleOn, setGoogleOn] = useState(false);
  // Une erreur Google ne concerne pas le champ saisi : elle s'affiche sous le bouton Google.
  const fieldError = error && error !== "googleOff" ? t(`auth.errors.${error}`) : null;

  useEffect(() => {
    isGoogleEnabled().then(setGoogleOn, () => setGoogleOn(false));
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const fail = (err: AuthErrorLike) => setError(authErrorKey(err, navigator.onLine));

  async function send(isResend = false) {
    setError(null);
    setNotice(null);
    setBusy("send");
    const { error: err } = await sendEmailCode(email.trim()).catch((e: AuthErrorLike) => ({ error: e }));
    setBusy(null);
    if (err) return fail(err);
    setStep("code");
    setCode("");
    setCooldown(RESEND_DELAY_S);
    setNotice(isResend ? t("auth.resent") : t("auth.codeSent", { email: email.trim() }));
  }

  async function verify(value: string) {
    if (busy) return;
    setError(null);
    setBusy("verify");
    const { error: err } = await verifyEmailCode(email.trim(), value).catch((e: AuthErrorLike) => ({
      error: e,
    }));
    setBusy(null);
    // En cas de succès, useSession reçoit la session et App affiche l'onboarding ou l'accueil.
    if (err) fail(err);
  }

  async function google() {
    setError(null);
    setBusy("google");
    try {
      const { error: err } = await signInWithGoogle();
      if (err) fail(err);
    } catch (e) {
      fail(e as AuthErrorLike);
    } finally {
      setBusy(null);
    }
  }

  return (
    <main
      style={{
        maxWidth: 480,
        margin: "0 auto",
        minHeight: "100dvh",
        display: "grid",
        gridTemplateRows: "auto 1fr",
      }}
    >
      {/* Image de terrain en haut, fondue dans le bleu nuit : la promesse avant le formulaire. */}
      <header
        style={{
          position: "relative",
          minHeight: "min(46dvh, 380px)",
          display: "grid",
          alignContent: "end",
          gap: 8,
          padding:
            "max(24px, env(safe-area-inset-top)) max(var(--sk-gutter), env(safe-area-inset-right)) 52px max(var(--sk-gutter), env(safe-area-inset-left))",
          color: "var(--sk-hero-text)",
          background: "var(--color-nuit)",
          overflow: "hidden",
        }}
      >
        <img
          src="/demo/hero.webp"
          alt=""
          fetchPriority="high"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
        />
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgb(27 27 57 / 0.25) 0%, rgb(27 27 57 / 0.55) 45%, #1b1b39 100%)",
          }}
        />
        <img
          src="/brand/logo-negative.svg"
          alt="SportKonnect"
          style={{ position: "relative", height: 34, width: "auto", justifySelf: "start" }}
        />
        <h1
          className="sk-h1"
          style={{
            position: "relative",
            fontSize: "clamp(24px, 8vw, 34px)",
            fontWeight: 700,
            color: "var(--sk-hero-text)",
          }}
        >
          {t("auth.title")}
        </h1>
        <p style={{ position: "relative", margin: 0, color: "var(--sk-hero-muted)" }}>{t("auth.intro")}</p>
      </header>

      <div
        style={{
          position: "relative",
          marginTop: -28,
          background: "var(--sk-bg)",
          borderRadius: "var(--sk-radius-lg) var(--sk-radius-lg) 0 0",
          padding:
            "24px max(var(--sk-gutter), env(safe-area-inset-right)) max(32px, env(safe-area-inset-bottom)) max(var(--sk-gutter), env(safe-area-inset-left))",
          display: "grid",
          gap: 16,
          alignContent: "start",
        }}
      >
        {install.visible && install.requested && (
          <Suspense fallback={null}>
            <InstallCard />
          </Suspense>
        )}
        {!online && (
          <Notice tone="warning" icon={<WifiOff size={18} />}>
            {t("auth.offlineNotice")}
          </Notice>
        )}

        {step === "email" ? (
          <form
            style={{ display: "grid", gap: 12 }}
            onSubmit={(e) => {
              e.preventDefault();
              void send();
            }}
          >
            <TextField
              label={t("auth.email")}
              type="email"
              required
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={fieldError}
            />
            <Button
              type="submit"
              busy={busy === "send"}
              busyLabel={t("auth.sending")}
              disabled={!online || busy !== null}
            >
              <Mail size={18} aria-hidden />
              {t("auth.sendCode")}
            </Button>
          </form>
        ) : (
          <form
            style={{ display: "grid", gap: 12 }}
            onSubmit={(e) => {
              e.preventDefault();
              void verify(code);
            }}
          >
            {notice && <Notice icon={<MailCheck size={18} />}>{notice}</Notice>}
            <TextField
              label={t("auth.code")}
              help={t("auth.codeHelp")}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              required
              autoFocus
              value={code}
              onChange={(e) => {
                const v = normalizeCode(e.target.value);
                setCode(v);
                if (v.length === 6) void verify(v); // validation automatique au 6e chiffre
              }}
              style={{ fontSize: 24, letterSpacing: 8, textAlign: "center" }}
              error={fieldError}
            />
            <Button
              type="submit"
              busy={busy === "verify"}
              busyLabel={t("auth.verifying")}
              disabled={!online || code.length !== 6 || busy !== null}
            >
              {t("auth.verify")}
            </Button>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
              <Button
                variant="link"
                disabled={cooldown > 0 || !online || busy !== null}
                onClick={() => void send(true)}
              >
                <RotateCw size={16} aria-hidden />
                {cooldown > 0 ? t("auth.resendIn", { seconds: cooldown }) : t("auth.resend")}
              </Button>
              <Button
                variant="link"
                disabled={busy !== null}
                onClick={() => {
                  setStep("email");
                  setError(null);
                  setNotice(null);
                }}
              >
                <ArrowLeft size={16} aria-hidden />
                {t("auth.changeEmail")}
              </Button>
            </div>
          </form>
        )}

        {step === "email" && (
          <>
            <p aria-hidden style={{ margin: 0, textAlign: "center", fontSize: 14 }}>
              {t("auth.or")}
            </p>
            <Button
              variant="secondary"
              busy={busy === "google"}
              busyLabel={t("auth.sending")}
              disabled={!googleOn || !online || busy !== null}
              onClick={() => void google()}
            >
              {t("auth.google")}
              {!googleOn && ` · ${t("auth.googleSoon")}`}
            </Button>
            {error === "googleOff" && <Notice role="alert">{t("auth.errors.googleOff")}</Notice>}
          </>
        )}
      </div>
    </main>
  );
}
